/**
 * WhatsApp Status Indicator
 *
 * Componente compatto per mostrare lo stato del bot WhatsApp nell'header principale.
 * - Verde: tutto OK (WhatsApp connesso + Bot attivo + Backend online + DB connesso)
 * - Rosso: qualcosa non va
 * - Cliccabile: apre modale con dettagli e azioni
 */

import { useState, useEffect } from 'react';
import { Wifi, AlertCircle, Server, Database, X, RefreshCw, Power } from 'lucide-react';
import { waAuthHeaders } from '../waFetch';

interface WhatsAppStatusIndicatorProps {
  apiBaseUrl?: string;
}

interface HealthData {
  status: string;
  database: string;
  whatsapp?: {
    connected: boolean;
    status: string;
  };
}

interface ConfigData {
  is_active: boolean;
}

export function WhatsAppStatusIndicator({ apiBaseUrl = '/api/wa' }: WhatsAppStatusIndicatorProps) {
  const [health, setHealth] = useState<HealthData | null>(null);
  const [config, setConfig] = useState<ConfigData | null>(null);
  const [connected, setConnected] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadStatus = async () => {
    try {
      // Load health (include WhatsApp connection status)
      const healthRes = await fetch(`${apiBaseUrl}/health`);
      if (healthRes.ok) {
        const healthData = await healthRes.json();
        setHealth(healthData);
        // Estrai stato connessione da health.whatsapp
        setConnected(healthData.whatsapp?.connected || false);
      }

      // Load config (bot active status)
      const configRes = await fetch(`${apiBaseUrl}/config`, { headers: waAuthHeaders() });
      if (configRes.ok) {
        const configData = await configRes.json();
        if (configData.success && configData.data) {
          setConfig(configData.data);
        }
      }
    } catch (err) {
      console.error('[WhatsAppStatusIndicator] Error loading status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    // Refresh ogni 30 secondi
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, [apiBaseUrl]);

  // Verde solo se TUTTO ok, altrimenti rosso
  // Nota: is_active può essere 1/0 (numero) o true/false (booleano)
  const isAllOk = connected &&
                  health?.status === 'healthy' &&
                  !!config?.is_active;

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700/50 border border-slate-600 h-[48px]">
        <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
        <span className="text-xs text-slate-400">WA Bot</span>
      </div>
    );
  }

  return (
    <>
      {/* Indicator Badge - Verde se tutto OK, Rosso altrimenti */}
      <button
        onClick={() => setShowModal(true)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all hover:scale-105 h-[48px] ${
          isAllOk
            ? 'bg-green-900/40 border-green-500/50 text-green-400'
            : 'bg-red-900/40 border-red-500/50 text-red-400 animate-pulse'
        }`}
        title="Stato WhatsApp Bot - Clicca per dettagli"
      >
        {isAllOk ? (
          <Wifi className="w-4 h-4" />
        ) : (
          <AlertCircle className="w-4 h-4" />
        )}
        <span className="text-xs font-semibold whitespace-nowrap">
          {isAllOk ? 'WA Bot OK' : 'WA Bot ⚠️'}
        </span>
      </button>

      {/* Status Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999]">
          <div className="bg-slate-800 border border-slate-600 rounded-xl p-5 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Wifi className="w-5 h-5 text-green-400" />
                WhatsApp Bot Status
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 hover:bg-slate-700 rounded-lg transition"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {/* Status Items */}
            <div className="space-y-2 mb-5">
              {/* WhatsApp Connection */}
              <div className={`flex items-center justify-between p-3 rounded-lg ${
                connected ? 'bg-green-900/30 border border-green-700/50' : 'bg-red-900/30 border border-red-700/50'
              }`}>
                <div className="flex items-center gap-2">
                  <Wifi className={`w-4 h-4 ${connected ? 'text-green-400' : 'text-red-400'}`} />
                  <span className="text-sm text-slate-200">WhatsApp</span>
                </div>
                <span className={`text-sm font-semibold ${connected ? 'text-green-400' : 'text-red-400'}`}>
                  {connected ? '● Connesso' : '○ Disconnesso'}
                </span>
              </div>

              {/* Bot Active */}
              <div className={`flex items-center justify-between p-3 rounded-lg ${
                config?.is_active ? 'bg-green-900/30 border border-green-700/50' : 'bg-red-900/30 border border-red-700/50'
              }`}>
                <div className="flex items-center gap-2">
                  <Power className={`w-4 h-4 ${config?.is_active ? 'text-green-400' : 'text-red-400'}`} />
                  <span className="text-sm text-slate-200">Bot Attivo</span>
                </div>
                <span className={`text-sm font-semibold ${config?.is_active ? 'text-green-400' : 'text-red-400'}`}>
                  {config?.is_active ? '● Attivo' : '○ Spento'}
                </span>
              </div>

              {/* Backend */}
              <div className={`flex items-center justify-between p-3 rounded-lg ${
                health?.status === 'healthy' ? 'bg-green-900/30 border border-green-700/50' : 'bg-red-900/30 border border-red-700/50'
              }`}>
                <div className="flex items-center gap-2">
                  <Server className={`w-4 h-4 ${health?.status === 'healthy' ? 'text-green-400' : 'text-red-400'}`} />
                  <span className="text-sm text-slate-200">Backend</span>
                </div>
                <span className={`text-sm font-semibold ${health?.status === 'healthy' ? 'text-green-400' : 'text-red-400'}`}>
                  {health?.status === 'healthy' ? '● Online' : '○ Offline'}
                </span>
              </div>

              {/* Database */}
              <div className={`flex items-center justify-between p-3 rounded-lg ${
                health?.database ? 'bg-green-900/30 border border-green-700/50' : 'bg-red-900/30 border border-red-700/50'
              }`}>
                <div className="flex items-center gap-2">
                  <Database className={`w-4 h-4 ${health?.database ? 'text-green-400' : 'text-red-400'}`} />
                  <span className="text-sm text-slate-200">Database</span>
                </div>
                <span className={`text-sm font-semibold ${health?.database ? 'text-green-400' : 'text-red-400'}`}>
                  {health?.database ? '● Connesso' : '○ Disconnesso'}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  loadStatus();
                }}
                className="flex-1 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Aggiorna
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  // Apri modal WhatsApp Bot se disponibile
                  if (typeof (window as any).openWhatsAppBotModal === 'function') {
                    (window as any).openWhatsAppBotModal();
                  }
                }}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition"
              >
                Gestione Bot
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default WhatsAppStatusIndicator;
